import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { CSSTransition } from 'react-transition-group';

import cls from 'classnames';

import noop from 'lodash/noop';

import {
  container as getContainer,
  ownerDocument as OwnerDocument,
  isOverflowing,
} from '@ott/utility-overlay';
import PopupManager from '@ott/utility-popup-manager';

import ContentContainer from '../Content/ContentContainer';
import { CLOSING_REASON, STATIC_MODAL_CLASSNAME } from '../constants';

import css from 'dom-helpers/style';
import getScrollbarSize from 'dom-helpers/util/scrollbarSize';

import styles from './Layout.scss';

const getId = (
  (c) => () =>
    ++c
)(-1);

const overlayAnimationClassNames = {
  appear: styles.overlayIsHidden,
  appearActive: styles.overlayIsShowing,
  appearDone: styles.overlayIsShowed,
  enter: styles.overlayIsHidden,
  enterActive: styles.overlayIsShowing,
  enterDone: styles.overlayIsShowed,
  exit: styles.overlayIsShowed,
  exitActive: styles.overlayIsHiding,
  exitDone: styles.overlayIsHidden,
};

const bodyAnimationClassNames = {
  appear: styles.bodyIsHidden,
  appearActive: styles.bodyIsShowing,
  appearDone: styles.bodyIsShowed,
  enter: styles.bodyIsHidden,
  enterActive: styles.bodyIsShowing,
  enterDone: styles.bodyIsShowed,
  exit: styles.bodyIsShowed,
  exitActive: styles.bodyIsHiding,
  exitDone: styles.bodyIsHidden,
};

const bottomSheetAnimationClassNames = {
  appear: styles.bottomSheetEnter,
  appearActive: styles.bottomSheetEnterActive,
  enter: styles.bottomSheetEnter,
  enterActive: styles.bottomSheetEnterActive,
  exit: styles.bottomSheetExit,
  exitActive: styles.bottomSheetExitActive,
};

class Layout extends PureComponent {
  static propTypes = {
    isOpen: PropTypes.bool,
    forcedLayoutStyles: PropTypes.object,
    isFullScreen: PropTypes.bool,
    isBottomSheet: PropTypes.bool,
    ContentComponent: PropTypes.func,
    overlayClassName: PropTypes.string,
    layoutContainerClassName: PropTypes.string,
    closeModal: PropTypes.func,
    className: PropTypes.string,
  };

  static defaultProps = {
    isOpen: false,
    contentOptions: {},
    isFullScreen: false,
    ContentComponent: ContentContainer,
    closeModal: noop,
    className: '',
  };

  constructor(props, ctx) {
    super(props, ctx);

    this._id = getId();
    this.state = {};
  }

  static instances = new Set();

  static instancesData = {};

  componentDidMount() {
    this.update();
  }

  componentWillUnmount() {
    this.remove();
  }

  componentWillReceiveProps(nextProps) {
    this.update(nextProps);
  }

  add() {
    const id = this._id;
    if (Layout.instances.has(id)) {
      return;
    }

    if (!Layout.instances.size) {
      this.blockBodyScroll();
    }

    Layout.instances.add(id);
    const container = getContainer(this.props.container, OwnerDocument(this).body);

    const data = {
      container,
      style: {
        overflow: container.style.overflow,
        paddingRight: container.style.paddingRight,
      },
    };

    const style = { overflow: 'hidden' };

    data.overflowing = isOverflowing(container);

    if (data.overflowing) {
      // use computed style, here to get the real padding
      // to add our scrollbar width
      const paddingRight = parseInt(css(container, 'paddingRight') || 0, 10) + getScrollbarSize();
      style.paddingRight = `${paddingRight}px`;
    }
    this.setState({ paddingRight: style.paddingRight || data.style.paddingRight });
    css(container, style);
    Layout.instancesData[id] = data;
  }

  remove() {
    const id = this._id;
    if (!Layout.instances.has(id)) {
      return;
    }
    Layout.instances.delete(id);
    const data = Layout.instancesData[id];
    delete Layout.instancesData[id];

    if (Layout.instances.size === 0) {
      css(data.container, {
        ...data.style,
        overflow: data.style.overflow === 'hidden' ? 'visible' : data.style.overflow,
        paddingRight: data.style.overflow === 'hidden' ? null : data.style.paddingRight,
      });

      this.unblockBodyScroll();
    }
  }

  update(props = this.props) {
    if (props.isOpen) {
      this.add();
    }
  }

  blockBodyScroll = () => {
    this.saveScrollPosition();
  };

  unblockBodyScroll = () => {
    this.restoreScrollPosition();
  };

  saveScrollPosition = () => {
    this.savedScrollPosition = {
      y: window.scrollY,
      x: window.scrollX,
    };
  };

  restoreScrollPosition = () => {
    const { savedScrollPosition } = this;

    if (!savedScrollPosition) {
      return;
    }

    const { x, y } = savedScrollPosition;
    window.scrollTo(x, y);

    delete this.savedScrollPosition;
  };

  handleOutsideClick = (event) => {
    const { closeOnOutsideClick, closeModal } = this.props;

    if (closeOnOutsideClick && PopupManager.getOpenedPopupsCount() === 0) {
      closeModal(true, CLOSING_REASON.OVERLAY);
    }
  };

  render() {
    const {
      className,
      overlayClassName,
      layoutContainerClassName,
      forcedLayoutStyles,
      isFullScreen,
      children,
      isOpen,
      closeModal,
      ContentComponent,
      contentOptions,
      contentOptions: { hasFixedHeaderAndFooter },
      isBottomSheet,
      layoutRef,
    } = this.props;

    if (!children) {
      return null;
    }

    return (
      <div
        className={cls(styles.Layout, STATIC_MODAL_CLASSNAME, className)}
        style={forcedLayoutStyles}
      >
        <div
          className={cls(styles.container, layoutContainerClassName, {
            [styles['container--isFullScreen']]: isFullScreen,
            [styles['container--isFullHeight']]: hasFixedHeaderAndFooter,
            [styles['container--isBottomSheet']]: isBottomSheet,
          })}
          ref={layoutRef}
        >
          <CSSTransition
            in={isOpen}
            appear={true}
            timeout={300}
            classNames={overlayAnimationClassNames}
          >
            <div
              onClick={this.handleOutsideClick}
              className={cls(overlayClassName, {
                [styles.overlay]: true,
                [styles.overlayFullscreen]: isFullScreen,
              })}
            />
          </CSSTransition>

          <CSSTransition
            in={isOpen}
            appear={true}
            timeout={300}
            classNames={isBottomSheet ? bottomSheetAnimationClassNames : bodyAnimationClassNames}
            onExited={this.remove}
          >
            <div className={cls(styles.body, isBottomSheet && styles.bottomSheetContainer)}>
              <ContentComponent
                isBottomSheet={isBottomSheet}
                isFullScreen={isFullScreen}
                closeModal={closeModal}
                {...contentOptions}
              >
                {children}
              </ContentComponent>
            </div>
          </CSSTransition>
        </div>
      </div>
    );
  }
}

export default Layout;
